import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
const { FiArrowLeft, FiExternalLink, FiMapPin, FiZap, FiGrid, FiTrendingUp, FiClock, FiEdit2, FiShare2, FiAward } = FiIcons;

function StatCard({ icon: Icon, label, value, color = '#ff00cc' }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <Icon className="mx-auto mb-1.5 text-lg" style={{ color }} />
      <div className="font-black text-white" style={{ fontSize: '22px' }}>{value}</div>
      <div className="text-gray-500 uppercase tracking-wider mt-0.5" style={{ fontSize: '11px' }}>{label}</div>
    </div>
  );
}

function BlockCard({ tile, onLocate }) {
  const pixels = tile.w * tile.h;
  const estimatedValue = (pixels * 0.10).toFixed(2);
  const shareText = encodeURIComponent(`🔥 I own ${pixels.toLocaleString()} pixels on the 1-Million-Pixel Grid!\n\nMy block: "${tile.title}"\n📍 [${tile.x}, ${tile.y}] — locked forever.\n\nClaim yours →`);
  const shareUrl = `https://twitter.com/intent/tweet?text=${shareText}`;

  return (
    <div className="rounded-2xl overflow-hidden transition-all hover:scale-[1.01]"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,0,200,0.2)' }}>
      {/* Preview */}
      <div className="relative h-32 overflow-hidden" style={{ background: tile.solidColor || '#0a0010' }}>
        {tile.image ? (
          <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" />
        ) : tile.blockText ? (
          <div className="w-full h-full flex items-center justify-center" style={{ background: tile.solidColor }}>
            <span className="font-black text-2xl" style={{ color: tile.blockTextColor || '#fff' }}>{tile.blockText}</span>
          </div>
        ) : (
          <div className="w-full h-full" style={{ background: tile.solidColor || '#1a0030' }} />
        )}
        {/* Badges */}
        <div className="absolute top-2 right-2 flex gap-1.5 flex-wrap justify-end">
          {tile.hasGlow && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase"
              style={{ background: 'rgba(255,0,200,0.8)', color: '#fff' }}>✨ GLOW</span>
          )}
          {tile.isDofollow && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase"
              style={{ background: 'rgba(0,255,136,0.8)', color: '#000' }}>🔗 DOFOLLOW</span>
          )}
          {tile.hasGif && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase"
              style={{ background: 'rgba(0,200,255,0.8)', color: '#000' }}>🎬 GIF</span>
          )}
        </div>
        {/* Coord pill */}
        <div className="absolute bottom-2 left-2">
          <span className="px-2 py-0.5 rounded-full font-mono text-[10px] text-gray-300"
            style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}>
            x:{tile.x} y:{tile.y}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-black text-white text-lg leading-tight" style={{ fontFamily: 'Impact,sans-serif' }}>{tile.title}</h3>
          <span className="text-xs font-black shrink-0 mt-1" style={{ color: '#ff00cc' }}>{tile.w}×{tile.h}px</span>
        </div>
        <a href={tile.link} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs mb-3 hover:text-pink-400 transition-colors truncate"
          style={{ color: '#6b7280' }}>
          <FiExternalLink className="shrink-0 text-[10px]" />
          <span className="truncate">{tile.link?.replace('https://', '')}</span>
        </a>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: 'Pixels', value: pixels.toLocaleString(), color: '#ff00cc' },
            { label: 'Est. Value', value: `$${estimatedValue}`, color: '#00ff88' },
            { label: 'Clicks', value: (tile.clicks || 0).toLocaleString(), color: '#00ffff' },
          ].map((s, i) => (
            <div key={i} className="rounded-lg p-2 text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="font-black" style={{ color: s.color, fontSize: '15px' }}>{s.value}</div>
              <div className="text-gray-600 uppercase tracking-wider" style={{ fontSize: '10px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={() => onLocate(tile.id)}
            className="flex-1 py-2 rounded-lg font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all hover:scale-105"
            style={{ background: 'rgba(255,0,200,0.12)', border: '1px solid rgba(255,0,200,0.3)', color: '#ff00cc' }}>
            <FiMapPin className="text-xs" /> Locate
          </button>
          <a href={tile.link} target="_blank" rel="noopener noreferrer"
            className="flex-1 py-2 rounded-lg font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all hover:scale-105"
            style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.25)', color: '#00ff88' }}>
            <FiExternalLink className="text-xs" /> Visit
          </a>
          <a href={shareUrl} target="_blank" rel="noopener noreferrer"
            className="py-2 px-3 rounded-lg font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all hover:scale-105"
            style={{ background: 'rgba(29,161,242,0.12)', border: '1px solid rgba(29,161,242,0.3)', color: '#1DA1F2' }}>
            <FiShare2 className="text-xs" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function MyBlocksPage() {
  const { tiles, user, setCurrentPage, locateTileOnGrid, setIsAuthModalOpen } = useAppContext();

  const myTiles = tiles.filter(t =>
    t.ownerEmail && user?.email && t.ownerEmail.toLowerCase() === user.email.toLowerCase()
  );

  const totalPixels = myTiles.reduce((acc, t) => acc + t.w * t.h, 0);
  const totalValue = (totalPixels * 0.10).toFixed(2);
  const totalClicks = myTiles.reduce((acc, t) => acc + (t.clicks || 0), 0);

  const handleLocate = (tileId) => {
    locateTileOnGrid(tileId);
    setCurrentPage('home');
  };

  return (
    <div className="min-h-screen" style={{ background: '#080012', fontFamily: 'Courier New, monospace', color: '#fff' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 px-6 pt-6 pb-4 border-b border-pink-500/20"
        style={{ background: 'rgba(8,0,18,0.97)', backdropFilter: 'blur(12px)' }}>
        <button onClick={() => setCurrentPage('home')}
          className="flex items-center gap-2 text-pink-500 hover:text-pink-300 transition-colors font-bold uppercase tracking-widest mb-4"
          style={{ fontSize: '13px' }}>
          <FiArrowLeft /> Back to Grid
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FiGrid className="text-pink-500 text-sm" />
              <span className="text-pink-500 text-xs font-black uppercase tracking-widest">Your Pixel Empire</span>
            </div>
            <h1 className="font-black uppercase leading-none" style={{ fontFamily: 'Impact, sans-serif', letterSpacing: '2px' }}>
              <span className="text-white" style={{ fontSize: 'clamp(28px,5vw,52px)' }}>MY </span>
              <span style={{ color: '#ff00cc', fontSize: 'clamp(28px,5vw,52px)' }}>BLOCKS</span>
            </h1>
            {user && (
              <p className="text-gray-500 text-sm mt-1 truncate max-w-xs">
                {user.user_metadata?.name || user.email}
              </p>
            )}
          </div>
          {!user && (
            <button onClick={() => setIsAuthModalOpen(true)}
              className="px-5 py-2.5 rounded-xl font-black text-sm uppercase tracking-widest text-white"
              style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontFamily: 'Impact,sans-serif' }}>
              Sign In
            </button>
          )}
        </div>
        <div className="mt-3 h-px w-full" style={{ background: 'linear-gradient(90deg,#ff00cc,transparent)' }} />
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
        {!user ? (
          /* Not signed in */
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(255,0,200,0.1)', border: '1px solid rgba(255,0,200,0.3)' }}>
              <FiGrid className="text-pink-500 text-2xl" />
            </div>
            <h2 className="font-black text-white text-2xl uppercase mb-2" style={{ fontFamily: 'Impact,sans-serif' }}>Sign in to see your blocks</h2>
            <p className="text-gray-500 mb-6">You need to be signed in to view your pixel blocks.</p>
            <button onClick={() => setIsAuthModalOpen(true)}
              className="px-8 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-white"
              style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}>
              Sign In / Sign Up
            </button>
          </div>
        ) : myTiles.length === 0 ? (
          /* Signed in but no blocks */
          <div>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <StatCard icon={FiGrid} label="Blocks Owned" value="0" />
              <StatCard icon={FiZap} label="Total Pixels" value="0" color="#00ffff" />
              <StatCard icon={FiTrendingUp} label="Est. Value" value="$0" color="#00ff88" />
              <StatCard icon={FiClock} label="Total Clicks" value="0" color="#f59e0b" />
            </div>
            <div className="text-center py-16 rounded-2xl" style={{ border: '1px dashed rgba(255,0,200,0.2)', background: 'rgba(255,0,200,0.02)' }}>
              <div className="text-5xl mb-4">🎯</div>
              <h2 className="font-black text-white text-2xl uppercase mb-2" style={{ fontFamily: 'Impact,sans-serif' }}>
                You haven't claimed any blocks yet.
              </h2>
              <p className="text-gray-500 mb-6">See how visitors engage with your ads.</p>
              <button onClick={() => setCurrentPage('home')}
                className="px-8 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-white inline-flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', boxShadow: '0 0 20px rgba(255,0,200,0.4)' }}>
                <FiZap /> Go grab some pixels
              </button>
            </div>
          </div>
        ) : (
          /* Has blocks */
          <div>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <StatCard icon={FiAward} label="Blocks Owned" value={myTiles.length} />
              <StatCard icon={FiZap} label="Total Pixels" value={totalPixels.toLocaleString()} color="#00ffff" />
              <StatCard icon={FiTrendingUp} label="Est. Value" value={`$${totalValue}`} color="#00ff88" />
              <StatCard icon={FiClock} label="Total Clicks" value={totalClicks.toLocaleString()} color="#f59e0b" />
            </div>

            {/* Achievement banner if big spender */}
            {totalPixels >= 1000 && (
              <div className="flex items-center gap-3 p-4 rounded-xl mb-6"
                style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.3)' }}>
                <span className="text-2xl">🏆</span>
                <div>
                  <div className="font-black text-yellow-400 uppercase tracking-wider" style={{ fontSize: '14px' }}>
                    Power Advertiser
                  </div>
                  <div className="text-gray-400 text-xs">You own {totalPixels.toLocaleString()}+ pixels. You're in the top tier!</div>
                </div>
              </div>
            )}

            {/* Blocks grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myTiles.map(tile => (
                <BlockCard key={tile.id} tile={tile} onLocate={handleLocate} />
              ))}
            </div>

            {/* Buy more CTA */}
            <div className="mt-10 text-center p-6 rounded-2xl"
              style={{ background: 'rgba(255,0,200,0.04)', border: '1px solid rgba(255,0,200,0.15)' }}>
              <p className="text-gray-400 mb-3 font-bold uppercase tracking-widest" style={{ fontSize: '12px' }}>
                Want more visibility?
              </p>
              <button onClick={() => setCurrentPage('home')}
                className="px-8 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-white inline-flex items-center gap-2 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', boxShadow: '0 0 20px rgba(255,0,200,0.3)' }}>
                <FiZap /> Claim More Pixels
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}